import React, { useCallback, useEffect } from 'react';

import { NavigationBar } from '@opendoor/bricks/complex/Modal/NavigationBar';
import { Box, Flex, Text } from '@opendoor/bricks/core';
import { bricks } from '@opendoor/bricks/system';
import { useTheme } from '@opendoor/bricks/system/theme';

import { AuthNavBarWidget } from 'platform/components/AuthNavBarWidget';
import { FullPageLayout, LayoutContent, useLayoutState } from 'platform/components/layout/Layout';
import { OpendoorLogo } from 'platform/components/OpendoorLogo';
import { useTOSContext } from 'platform/components/TOSProvider';
import { useObservability } from 'platform/observability';
import { LegacyExternalLink } from 'platform/remix-run/bricks';

import { CXA_EMAIL_NEW as CXA_EMAIL } from 'shared/helpers/globals';
import { normalizePathname, searchParamsToObject } from 'shared/helpers/hooks/usePath';

type ErrorCause = 'user-error' | 'system-error';

export interface HubGenericErrorPageProps {
  reason: string;
  // Is the error expected and caused by the user (e.g. unauthenticated or not
  // authorized), or is it unexpected and caused by a bug or system issue (e.g.
  // an outage)? This is used for metrics purposes.
  causedBy: ErrorCause;
  error?: Error;
  headerContent?: string;
  errorContent?: React.ReactNode;
}
/**
 * Generic Error Page with automatic tracking
 */
export const GenericErrorPage: React.FC<HubGenericErrorPageProps> = ({
  reason,
  causedBy,
  headerContent = 'Oops, something went wrong',
  errorContent,
}) => {
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  useEffect(
    () => trackGenericErrorPageView(reason, causedBy),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [],
  );

  return <ErrorPageContent errorContent={errorContent} headerContent={headerContent} />;
};

export const ErrorPageContent: React.FC<
  Pick<HubGenericErrorPageProps, 'errorContent' | 'headerContent'>
> = ({ errorContent, headerContent = 'Oops, something went wrong' }) => {
  const state = useLayoutState();
  const { forceHideTOSModal } = useTOSContext();

  // DO NOT REMOVE: Do not show the TOS modal on this page
  useEffect(() => {
    // set this as false on the initial render - so it doesn't show it on this page
    forceHideTOSModal(true);
    return () => {
      // allow the TOS component to be shown when we unmount
      forceHideTOSModal(false);
    };
  }, [forceHideTOSModal]);

  return (
    <FullPageLayout>
      <NavigationBar
        leftComponent={<OpendoorLogo variant="primary" height="24px" />}
        rightComponent={<AuthNavBarWidget />}
      />
      <LayoutContent>
        <Flex flexDir="column" alignItems="center" data-testid="generic-error-page">
          <Box mt={8}>
            <ErrorImage size={state === 'mobile' ? 120 : 180} />
          </Box>
          <Text mt={6} mb={5} fontSize={['s3', null, 's5']} fontWeight="bold" textAlign="center">
            {headerContent}
          </Text>
          {errorContent ? (
            errorContent
          ) : (
            <Text textAlign="center">
              It looks like we ran into an issue. Please try again.
              <br />
              Email us at{' '}
              <LegacyExternalLink
                href={`mailto:${CXA_EMAIL}`}
                analyticsName="cake-generic-error-page-email-opendoor"
                aria-label={null}
              >
                {CXA_EMAIL}
              </LegacyExternalLink>{' '}
              if the problem persists.
            </Text>
          )}
        </Flex>
      </LayoutContent>
    </FullPageLayout>
  );
};

/**
 * Redirect to generic error route with metrics
 */
export const useTrackGenericErrorPageViewEvent = () => {
  const { trackEvent } = useObservability();
  const location =
    typeof window !== 'undefined' ? window.location : { pathname: '', search: '', hash: '' };
  const normalizedPath = normalizePathname(location.pathname);

  const trackGenericErrorPageView = useCallback(
    (reason: string, causedBy: ErrorCause, data?: Record<string, unknown>) => {
      trackEvent(
        'error-page-view',
        normalizedPath,
        {
          ...data,
          team: data?.team ?? 'consumer-platform',
          reason,
          causedBy,
          path: normalizedPath,
          pathname: location.pathname,
          hash: location.hash,
          queryParams: searchParamsToObject(location.search),
        },
        { addTiming: true },
      );
    },
    // Don't put trackEvent in deps array here because the contextual data could change often.
    // Don't put location here because we already have normalizedPath.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [normalizedPath],
  );

  return { trackGenericErrorPageView };
};

const ErrorImage: React.FC<{ size: number }> = ({ size }) => {
  const { colors } = useTheme();
  const brand50 = colors.brand50 as string;
  const neutrals100 = colors.neutrals100 as string;
  // NOTE: This is a nonstandard theme color that is only used here.
  const brand10 = '#B0D5F7';
  const terracotta50 = colors.terracotta50 as string;
  return (
    <bricks.svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M62.2828 63.2756L57.0431 68.5153H54.3983V57.6367H49.1587H34.9367C33.7391 57.6367 32.5914 58.1357 31.7431 58.9342L27.252 63.4253H43.3701C44.5677 63.4253 45.7155 63.9243 46.5638 64.7227L54.2486 72.4076H69.9676L84.0897 58.2854C84.5388 57.8363 85.0878 57.6367 85.6866 57.6367H83.2913H62.2828V63.2756Z"
        fill={neutrals100}
      />
      <path
        d="M46.3634 83.6355H48.609C49.2078 83.6355 49.7069 83.1365 49.7069 82.5377V75.801C49.7069 75.2021 49.2078 74.7031 48.609 74.7031H46.3634C45.7646 74.7031 45.2656 75.2021 45.2656 75.801V82.5377C45.2656 83.1365 45.7646 83.6355 46.3634 83.6355Z"
        fill={brand10}
      />
      <path
        d="M16.0741 63.4258L7.0918 72.408V106.092C7.0918 108.587 9.08786 110.583 11.5829 110.583V99.3548C11.5829 96.8598 13.579 94.8637 16.0741 94.8637C18.5691 94.8637 20.5652 96.8598 20.5652 99.3548V110.583H25.0563V72.408L16.0741 63.4258ZM18.3196 86.9793C18.3196 87.5781 17.8206 88.0771 17.2218 88.0771H14.9762C14.3774 88.0771 13.8784 87.5781 13.8784 86.9793V75.6516C13.8784 75.0528 14.3774 74.5538 14.9762 74.5538H17.2218C17.8206 74.5538 18.3196 75.0528 18.3196 75.6516V86.9793Z"
        fill={brand10}
      />
      <path
        d="M37.7318 42.4671C39.179 40.7205 41.3746 39.6227 43.8198 39.6227C45.8657 39.6227 47.762 40.4211 49.1592 41.7186V23.1552H44.6182C43.7699 23.1552 43.4206 22.1073 44.0693 21.6083L53.9498 14.123H46.0155C33.59 14.123 22.961 21.6582 18.4199 32.4369C19.8172 32.0377 21.2643 31.8381 22.8113 31.8381C29.7476 31.6884 35.636 36.2294 37.7318 42.4671Z"
        fill={brand10}
      />
      <path
        d="M37.383 83.6355H41.8741C42.4729 83.6355 42.972 83.1365 42.972 82.5377V75.801C42.972 75.2021 42.4729 74.7031 41.8741 74.7031H37.383C36.7841 74.7031 36.2852 75.2021 36.2852 75.801V82.5377C36.2852 83.1365 36.7841 83.6355 37.383 83.6355Z"
        fill={brand10}
      />
      <path
        d="M56.495 99.3545C54 99.3545 52.0039 101.351 52.0039 103.846V110.582H60.9862V103.846C60.9862 101.351 58.9901 99.3545 56.495 99.3545Z"
        fill={brand10}
      />
      <path d="M29.4473 85.8809V110.582H40.7749V97.1087L29.4473 85.8809Z" fill={brand10} />
      <path
        d="M30.6447 83.6355H32.8903C33.4891 83.6355 33.9881 83.1365 33.9881 82.5377V75.801C33.9881 75.2021 33.4891 74.7031 32.8903 74.7031H30.6447C30.0459 74.7031 29.5469 75.2021 29.5469 75.801V82.5377C29.5469 83.1365 30.0459 83.6355 30.6447 83.6355Z"
        fill={brand10}
      />
      <path
        d="M95.7669 97.0596H75.5568C74.958 97.0596 74.459 97.5586 74.459 98.1574V110.583H84.9383C89.1799 110.583 93.2219 109.685 96.9146 108.088V98.1574C96.9146 97.5586 96.4156 97.0596 95.7669 97.0596Z"
        fill={brand10}
      />
      <path
        d="M54.248 72.4082V85.8816L65.4759 97.1094V110.583H69.967V72.4082H54.248Z"
        fill={brand10}
      />
      <path
        d="M88.3805 21.5079C89.0292 22.0069 88.6799 23.0548 87.8315 23.0548H83.2905V57.5866H85.6858C86.2347 57.5866 86.8335 57.7862 87.2826 58.2354L101.405 72.3575V80.9405C103.151 80.0922 104.998 79.4934 106.994 79.194C106.994 79.1441 106.994 79.0942 106.994 78.9944C106.994 74.104 110.337 70.0121 114.878 68.8145V42.1671H98.6103H90.0272C89.2288 42.1671 88.5801 41.4186 88.7298 40.6201C89.4284 37.0272 92.6221 34.2826 96.4645 34.2826C98.9097 34.2826 101.105 35.3805 102.552 37.127C104.399 31.6878 109.09 27.5959 114.878 26.5978V13.9229H78.5L88.3805 21.5079Z"
        fill={brand10}
      />
      <path
        d="M31.7921 58.9343C32.6404 58.0859 33.7882 57.6368 34.9858 57.6368H49.2077V47.5068H41.7225H16.123V63.4254H27.3509L31.7921 58.9343Z"
        fill={brand10}
      />
      <path
        d="M54.249 85.8816V72.4082H25.0566V110.583H29.4479V85.8816H54.249ZM45.2667 75.7516C45.2667 75.1528 45.7657 74.6538 46.3645 74.6538H48.6101C49.2089 74.6538 49.708 75.1528 49.708 75.7516V82.4883C49.708 83.0871 49.2089 83.5861 48.6101 83.5861H46.3645C45.7657 83.5861 45.2667 83.0871 45.2667 82.4883V75.7516ZM36.2845 75.7516C36.2845 75.1528 36.7835 74.6538 37.3823 74.6538H41.8734C42.4722 74.6538 42.9713 75.1528 42.9713 75.7516V82.4883C42.9713 83.0871 42.4722 83.5861 41.8734 83.5861H37.3823C36.7835 83.5861 36.2845 83.0871 36.2845 82.4883V75.7516ZM29.5478 75.7516C29.5478 75.1528 30.0468 74.6538 30.6456 74.6538H32.8911C33.49 74.6538 33.989 75.1528 33.989 75.7516V82.4883C33.989 83.0871 33.49 83.5861 32.8911 83.5861H30.6456C30.0468 83.5861 29.5478 83.0871 29.5478 82.4883V75.7516Z"
        fill="neutrals0"
      />
      <path
        d="M40.7754 97.1094V110.583H52.0032V103.846C52.0032 101.351 53.9993 99.3549 56.4944 99.3549C58.9894 99.3549 60.9855 101.351 60.9855 103.846V110.583H65.4766V97.1094H40.7754Z"
        fill="neutrals0"
      />
      <path
        d="M101.405 72.4076L87.2826 58.2854C86.8335 57.8363 86.2846 57.6367 85.6858 57.6367C85.0869 57.6367 84.538 57.8363 84.0889 58.2854L69.9668 72.4076V110.582H74.4579V98.1567C74.4579 97.5579 74.9569 97.0589 75.5557 97.0589H95.7658C96.3647 97.0589 96.8637 97.5579 96.8637 98.1567V108.087C98.4107 107.389 99.9576 106.59 101.355 105.642V80.9906V72.4076H101.405ZM87.9313 82.4877C87.9313 83.0865 87.4323 83.5855 86.8335 83.5855H84.5879C83.9891 83.5855 83.4901 83.0865 83.4901 82.4877V71.2598C83.4901 70.661 83.9891 70.162 84.5879 70.162H86.8335C87.4323 70.162 87.9313 70.661 87.9313 71.2598V82.4877Z"
        fill="neutrals0"
      />
      <path
        d="M102.553 37.1776C101.106 35.4311 98.9099 34.3333 96.4647 34.3333C92.6223 34.3333 89.4286 37.0778 88.73 40.6707C88.5803 41.4692 89.1791 42.2177 90.0274 42.2177H98.6105H114.878V26.6484C109.09 27.6465 104.349 31.7384 102.553 37.1776Z"
        fill="neutrals0"
      />
      <path
        d="M49.1588 47.5072V41.7186C47.7615 40.4211 45.8653 39.6227 43.8193 39.6227C41.3742 39.6227 39.1785 40.7206 37.7314 42.4671C35.6355 36.2294 29.7471 31.7383 22.7609 31.7383C21.214 31.7383 19.7668 31.9379 18.3696 32.3371C12.2317 34.1336 7.64079 39.5229 7.04197 46.06C6.99207 46.8085 7.59087 47.5072 8.3394 47.5072H16.0242H41.6237H49.1588Z"
        fill={brand50}
      />
      <path
        d="M61.0145 10.5683C62.6095 8.33109 65.9334 8.33109 67.5285 10.5683L94.5362 48.45C96.424 51.0978 94.5311 54.7721 91.2792 54.7721H37.2637C34.0119 54.7721 32.119 51.0978 34.0067 48.45L61.0145 10.5683Z"
        fill={terracotta50}
      />
      <rect x="61" y="18" width="6" height="22" rx="3" fill="neutrals0" />
      <circle cx="64" cy="46" r="3" fill="neutrals0" />
      <path d="M62.2826 63.2763V57.5875V52.7471H57.043V68.5159L62.2826 63.2763Z" fill={brand10} />
      <path
        d="M106.995 79.0451C106.995 79.095 106.995 79.1449 106.995 79.2447C104.999 79.5442 103.103 80.143 101.406 80.9913V105.643C107.295 101.75 111.736 95.812 113.732 88.8258C114.48 86.2309 114.88 83.4864 114.88 80.642V68.8652C110.339 70.013 106.995 74.1548 106.995 79.0451Z"
        fill={brand50}
      />
      <path d="M57.0432 68.5159V52.7471H54.3984V57.5875V68.5159H57.0432Z" fill={brand50} />
      <path
        d="M54.248 72.408L46.5631 64.7232C45.7148 63.8749 44.567 63.4258 43.3694 63.4258H27.2513H16.0234L25.0057 72.408H54.248Z"
        fill={brand50}
      />
      <path d="M40.7749 97.1087H65.4762L54.2483 85.8809H29.4473L40.7749 97.1087Z" fill={brand50} />
      <path
        d="M16.0751 94.8633C13.58 94.8633 11.584 96.8593 11.584 99.3544V110.582H20.5662V99.3544C20.5662 96.8593 18.5702 94.8633 16.0751 94.8633Z"
        fill={brand50}
      />
      <path
        d="M86.7848 70.1621H84.5392C83.9404 70.1621 83.4414 70.6611 83.4414 71.2599V82.4878C83.4414 83.0866 83.9404 83.5856 84.5392 83.5856H86.7848C87.3836 83.5856 87.8826 83.0866 87.8826 82.4878V71.2599C87.9325 70.6611 87.4335 70.1621 86.7848 70.1621Z"
        fill={brand50}
      />
      <path
        d="M17.1715 74.5537H14.9259C14.3271 74.5537 13.8281 75.0527 13.8281 75.6515V86.9792C13.8281 87.578 14.3271 88.077 14.9259 88.077H17.1715C17.7703 88.077 18.2694 87.578 18.2694 86.9792V75.6515C18.3193 75.0527 17.8202 74.5537 17.1715 74.5537Z"
        fill={brand50}
      />
    </bricks.svg>
  );
};
